import { Text } from 'blaze-ui/Text/Text'
import Styles from './BrandVisualStyleSelection.module.scss'
import cx from 'classnames'
import { MediaFile } from 'components/VisualStylePreviewCarousel/VisualStylePreviewCarousel'
import { useProgress } from 'hooks/useProgress'
import { useEffect } from 'react'
import { LoadingEllipses } from 'components/Wizards/Wizard/Form/SectionHeader'
import { ProgressBar } from 'components/ProgressBar/ProgressBar'

export interface GenerationLoadingProps {
  sourceImage: MediaFile
  stylesGeneration: { styledUrl?: string | null; isLoading: boolean; hasFailed: boolean }
  onTransformedImageLoaded: (loaded: boolean) => void
  isLoading: boolean
  withTag?: boolean
}

export const GenerationLoading = ({
  sourceImage,
  stylesGeneration,
  onTransformedImageLoaded,
  isLoading,
  withTag = true,
}: GenerationLoadingProps) => {
  const {
    start: startThree,
    progress: progressThree,
    isRunning: isRunningThree,
  } = useProgress({
    linearProgress: true,
    immediateComplete: false,
    speed: 40,
  })

  const {
    start: startTwo,
    progress: progressTwo,
    isRunning: isRunningTwo,
  } = useProgress({
    linearProgress: true,
    immediateComplete: true,
    speed: 40,
    onDone: startThree,
  })

  const {
    start: startOne,
    progress: progressOne,
    isRunning: isRunningOne,
  } = useProgress({
    linearProgress: true,
    immediateComplete: true,
    speed: 40,
    onDone: startTwo,
  })

  const progresses = [
    {
      title: 'Analyzing source image',
      isRunning: isRunningOne,
      percentComplete: progressOne,
    },
    { title: 'Applying style', isRunning: isRunningTwo, percentComplete: progressTwo },
    { title: 'Generating new image', isRunning: isRunningThree, percentComplete: progressThree },
  ]

  useEffect(() => {
    startOne()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const transformedImage = stylesGeneration.styledUrl
  const originalImage = sourceImage.url

  return (
    <div className={cx(Styles.loadingContainer, { [Styles.loading]: isLoading })}>
      <img
        src={originalImage || ''}
        alt="transforming original"
        className={cx(Styles.transformedImage, { [Styles.visible]: isLoading })}
      />
      <img
        src={transformedImage || ''}
        alt="real transformed"
        onLoad={() => onTransformedImageLoaded(true)}
        className={cx(Styles.transformedImage, { [Styles.visible]: !isLoading })}
      />
      {withTag && <div className={cx(Styles.imageTag, Styles.generatedTag)}>Generated Image</div>}
      {isLoading && (
        <>
          {progresses.map((progress) => {
            const isRunning = progress.isRunning
            const isCompleted = !progress.isRunning && progress.percentComplete === 100
            const totalProgress = progresses.reduce((acc, curr) => acc + curr.percentComplete, 0)

            if (!isRunning || isCompleted) return null

            return (
              <div className={Styles.progressContainer} key={progress.title}>
                <ProgressBar
                  percentage={totalProgress / 3}
                  color="var(--light-100)"
                  backgroundColor="var(--dark-15)"
                  size="xs"
                  width="112px"
                />
                <Text color="var(--light-60)">
                  {progress.title}
                  <LoadingEllipses />
                </Text>
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
